/**
 * Сохранённый наряд — просмотр и ПРАВКА.
 *
 * Прежде сохранённый наряд можно было только посмотреть и скачать: заметил
 * опечатку в фамилии, неверную дату или цену элемента — формируй отчёт
 * заново из заказов и удаляй старый. Теперь он правится тем же листом, что и
 * в «Заказах», а «Сохранить изменения» обновляет этот же снимок.
 *
 * Правится именно СНИМОК: заказы при этом не меняются, как и при правке
 * отчёта в «Заказах». Черновик живёт в окне, пока не нажата кнопка сохранения;
 * печать и выгрузка берут то, что сейчас на экране, — в том числе ещё не
 * сохранённое, чтобы не приходилось сохранять ради пробной печати.
 */

import { useMemo, useState } from 'react';
import { FileDown, FileText, Printer, Save, Send } from 'lucide-react';
import { SendReportDialog } from '../components/SendDialogs.js';
import { buildReport, formatKop, itemsTotalKop, renderReportPage, sortReportRows, type ReportItem } from '@otc/shared';
import { api, downloadReport } from '../api/client.js';
import { useFacsimile, type ApiReport, type ApiReportRow } from '../api/queries.js';
import { Button, Modal } from '../components/ui.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { printHtml } from '../components/printHtml.js';
import { isNarrow } from '../components/isNarrow.js';
import {
  ReportColumnsControls,
  ReportEditToggle,
  ReportSheet,
  type SheetColumns,
  type SheetGroup,
  type SheetHeaderPatch,
} from '../components/ReportSheet.js';

interface DraftRow extends ApiReportRow {
  /** Ключ строки в листе: у одного заказа в снимке могло оказаться две строки. */
  key: string;
}

interface Draft {
  clinicName: string;
  legalName: string;
  dateIso: string;
  responsible: string;
  columns: SheetColumns;
  rows: DraftRow[];
}

function draftOf(report: ApiReport): Draft {
  return {
    clinicName: report.clinicName,
    legalName: report.legalName,
    dateIso: report.date,
    responsible: report.responsible,
    columns: {
      showReceived: report.showReceived,
      showDue: report.showDue,
      showSubtotal: report.showSubtotal,
      facsimile: report.facsimile ?? false,
      // Развёрнутый ли наряд, видно по строкам: у развёрнутого есть элементы.
      detailed: report.rows.some((row) => (row.items?.length ?? 0) > 0),
    },
    rows: report.rows.map((row, index) => ({ ...row, key: `${row.orderId}-${index}` })),
  };
}

/**
 * Элементы строки: сохранённые — или сама работа целиком.
 *
 * Обычный наряд, в котором включили «расписать по элементам», элементов не
 * имеет: начинать приходится с одной строки — работы и её цены, — которую
 * дальше расписывают руками.
 */
function itemsOfRow(row: ApiReportRow): ReportItem[] {
  return row.items && row.items.length > 0
    ? row.items
    : [{ name: row.construction, qty: 1, priceKop: row.costKop }];
}

export function ReportViewModal({
  report,
  onClose,
  onSaved,
  onError,
  onNotify,
}: {
  report: ApiReport;
  onClose: () => void;
  /** Снимок обновлён на сервере — стоит перечитать список и сказать об этом. */
  onSaved: (message: string) => void;
  onError: (message: string) => void;
  /** Сказать что-то обычным тоном, ничего не меняя: например, «письма ушли». */
  onNotify?: (message: string) => void;
}) {
  const [draft, setDraft] = useState<Draft>(() => draftOf(report));
  const [editMode, setEditMode] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const узкий = isNarrow();
  const facsimile = useFacsimile();

  const change = (update: (current: Draft) => Draft) => {
    setDraft(update);
    setDirty(true);
  };

  const { detailed } = draft.columns;

  /* Сумма строки в развёрнутом наряде — всегда сумма её элементов. */
  const costOf = (row: ApiReportRow) => (detailed ? itemsTotalKop(itemsOfRow(row)) : row.costKop);

  /** Группировка по врачам — врачи и пациенты по алфавиту, как в PDF и Excel. */
  const groups: SheetGroup[] = useMemo(() => {
    const map = new Map<string, DraftRow[]>();
    for (const row of sortReportRows(draft.rows)) {
      const name = row.doctor || '—';
      map.set(name, [...(map.get(name) ?? []), row]);
    }
    return [...map.entries()].map(([name, rows]) => ({
      key: name,
      name,
      subtotalKop: rows.reduce((sum, row) => sum + costOf(row), 0),
      rows: rows.map((row) => ({
        key: row.key,
        patient: row.patient,
        construction: row.construction,
        received: row.received,
        due: row.due,
        costKop: costOf(row),
        items: detailed ? itemsOfRow(row) : [],
      })),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  const totalKop = draft.rows.reduce((sum, row) => sum + costOf(row), 0);

  /** Тело запроса — одинаковое для сохранения, печати и выгрузки. */
  const payload = () => ({
    clinicName: draft.clinicName,
    legalName: draft.legalName,
    periodLabel: report.periodLabel,
    responsible: draft.responsible,
    ...draft.columns,
    date: draft.dateIso,
    rows: draft.rows.map((row) => ({
      orderId: row.orderId,
      doctor: row.doctor,
      patient: row.patient,
      construction: row.construction,
      received: row.received,
      due: row.due,
      costKop: costOf(row),
      items: detailed ? itemsOfRow(row) : undefined,
    })),
  });

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/api/reports/${report.id}`, payload());
      setDirty(false);
      setEditMode(false);
      onSaved(`Наряд «${draft.clinicName}» сохранён`);
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Не удалось сохранить наряд');
    } finally {
      setBusy(false);
    }
  };

  const download = async (format: 'xlsx' | 'pdf') => {
    setBusy(true);
    try {
      await downloadReport(payload(), format);
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Не удалось выгрузить');
    } finally {
      setBusy(false);
    }
  };

  /* Несохранённые правки молча не теряются: закрытие спрашивает. */
  const requestClose = () => (dirty ? setConfirmClose(true) : onClose());

  const onHeader = (patch: SheetHeaderPatch) =>
    change((current) => ({
      ...current,
      ...(patch.clinicName !== undefined && { clinicName: patch.clinicName }),
      ...(patch.legalName !== undefined && { legalName: patch.legalName }),
      // Очищенная дата наряда не принимается: без даты у листа нет шапки.
      ...(patch.dateIso ? { dateIso: patch.dateIso } : {}),
      ...(patch.responsible !== undefined && { responsible: patch.responsible }),
    }));

  /** Окно выбора получателей. */
  const [отправка, setОтправка] = useState(false);

  return (
    <>
    <Modal
      title={`Наряд · ${draft.clinicName}`}
      onClose={requestClose}
      size="2xl"
      footer={
        <>
          <Button onClick={() => void save()} disabled={busy || !dirty}>
            <Save size={14} /> {busy ? 'Сохранение…' : 'Сохранить изменения'}
          </Button>
          <Button
            variant="secondary"
            onClick={() => printHtml(renderReportPage(buildReport({ ...payload(), facsimileImages: facsimile })))}
            disabled={busy}
          >
            <Printer size={14} /> Печать
          </Button>
          <Button variant="secondary" onClick={() => void download('pdf')} disabled={busy}>
            <FileDown size={14} /> Скачать PDF
          </Button>
          <Button variant="secondary" onClick={() => void download('xlsx')} disabled={busy}>
            <FileText size={14} /> Скачать Excel
          </Button>
          {/* Тот же наряд, что на экране, — письмом врачу или в клинику. */}
          <Button variant="secondary" onClick={() => setОтправка(true)} disabled={busy}>
            <Send size={14} /> Отправить
          </Button>
          <Button variant="ghost" onClick={requestClose}>Закрыть</Button>
          <span className="ml-auto self-center text-sm text-ink2">
            Строк: {draft.rows.length} · <span className="tabular text-ink">{formatKop(totalKop)} ₽</span>
            {dirty && (
              <span className="ml-2 text-[11px]" style={{ color: 'var(--otc-amber)' }}>
                · есть несохранённые правки
              </span>
            )}
          </span>
        </>
      }
    >
      <ReportEditToggle
        editMode={editMode}
        onToggle={() => setEditMode((v) => !v)}
        narrow={узкий}
        note="Правится сохранённый наряд; заказы не меняются"
      />

      <ReportColumnsControls
        facsimileAvailable={Boolean(facsimile)}
        columns={draft.columns}
        onChange={(patch) => change((current) => ({ ...current, columns: { ...current.columns, ...patch } }))}
      />

      <ReportSheet
        editMode={editMode}
        narrow={узкий}
        clinicName={draft.clinicName}
        legalName={draft.legalName}
        dateIso={draft.dateIso}
        responsible={draft.responsible}
        onHeader={onHeader}
        columns={draft.columns}
        groups={groups}
        totalKop={totalKop}
        onRow={(key, patch) =>
          change((current) => ({
            ...current,
            rows: current.rows.map((row) => (row.key === key ? { ...row, ...patch } : row)),
          }))
        }
        onItems={(key, items) =>
          change((current) => ({
            ...current,
            rows: current.rows.map((row) =>
              row.key === key ? { ...row, items, costKop: itemsTotalKop(items) } : row,
            ),
          }))
        }
        emptyText="В наряде нет строк"
        facsimileImages={facsimile}
      />

      {confirmClose && (
        <ConfirmDialog
          title="Закрыть без сохранения?"
          description="Правки в этом наряде пропадут. Сохранённый снимок останется прежним."
          confirmLabel="Закрыть без сохранения"
          onConfirm={() => {
            setConfirmClose(false);
            onClose();
          }}
          onCancel={() => setConfirmClose(false)}
        />
      )}
    </Modal>

    {/* Получатели и письмо: тот же наряд уходит вложением в PDF. */}
    {отправка && (
      <SendReportDialog
        report={payload()}
        onClose={() => setОтправка(false)}
        onDone={(текст) => {
          setОтправка(false);
          /*
            Весть об УСПЕШНОЙ отправке — обычным тоном, а не ошибкой.

            Здесь звался `onError`, и «Отправлено: 3» выходило красной
            плашкой — как будто что-то сломалось. Само окно при этом не
            закрывается: отправка наряд не меняет.
          */
          (onNotify ?? onSaved)(текст);
        }}
      />
    )}
    </>
  );
}
